/**
 * Wrapper for JournalEntryForm that handles create mutation internally.
 * Used by QuickCreateMenu to create new journal entries.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { navigateToEntity } from '../../shared/utils/entityUrls';
import { JournalEntryForm } from '../../modules/accounting/components/JournalEntryForm';
import { useCreateJournalEntry } from '../../modules/accounting/hooks/useJournalEntries';
import { useNotification } from '../../shared/context/NotificationContext';
import type { JournalEntryCreate } from '../../modules/accounting/types';

interface QuickCreateJournalEntryFormProps {
  open: boolean;
  onClose: () => void;
}

export function QuickCreateJournalEntryForm({ open, onClose }: QuickCreateJournalEntryFormProps) {
  const { t } = useTranslation('accounting');
  const { showSuccess } = useNotification();
  const navigate = useNavigate();
  const createEntry = useCreateJournalEntry();

  const handleSubmit = async (data: JournalEntryCreate) => {
    // A failure is deliberately NOT caught here: it propagates to the dialog,
    // which shows the reason inline (a closed fiscal period names the period)
    // and keeps the user's work.
    const created = await createEntry.mutateAsync(data);
    showSuccess(t('journalEntries.createSuccess'));
    onClose();
    navigateToEntity(navigate, 'accounting_journal_entries', created.data.id);
  };

  return (
    <JournalEntryForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      entry={null}
      isLoading={createEntry.isPending}
    />
  );
}
